import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { hasStoredDevUser, isPrerender } from '../auth-token.storage';

export const authGuard: CanActivateFn = (_route, state) => {
  const auth = inject(AuthService);
  const router = inject(Router);

  // No identity in localStorage → signed out, whatever the token says.
  //
  // Tokens live in sessionStorage and identity (`beacon_dev_user`) in
  // localStorage, so the two have different lifetimes and can drift apart:
  // another tab signing out, or the key being cleared by hand, leaves this tab
  // holding a live token with nobody behind it. Running on a stale identity is
  // worse than a forced sign-in — every signed-in path reads the dev user for
  // permissions, shelf scope and company.
  //
  // Checked before the token so that state can't slip through on a token alone.
  // Every real sign-in writes the key (setAuthenticated → selectUser), so a
  // genuinely authenticated user always has it.
  //
  // Prerender is exempt for the same reason endSessionLocally() exempts it: a
  // headless PDF render injects a token before boot and has no dev user, and
  // bouncing it to /login aborts the export.
  if (!isPrerender() && !hasStoredDevUser()) {
    auth.clearCredentials();
    auth.pendingReturnUrl.set(state.url);
    return router.createUrlTree(['/login']);
  }

  // Let the user through when they have a valid (present, unexpired) token, or
  // a refresh token the interceptor can silently renew with. Otherwise the
  // session is expired/unrecoverable → send them to login (with return URL) so
  // they never land on a blank, half-loaded protected page.
  if (auth.isSessionValid() || auth.refreshToken()) return true;

  auth.pendingReturnUrl.set(state.url);
  return router.createUrlTree(['/login']);
};

export const publicGuard: CanActivateFn = () => {
  const auth = inject(AuthService);
  const router = inject(Router);

  if (!auth.isAuthenticated()) return true;

  return router.createUrlTree([auth.pendingReturnUrl()]);
};

export const mfaGuard: CanActivateFn = () => {
  const auth = inject(AuthService);
  const router = inject(Router);

  if (auth.pendingUserId()) return true;

  return router.createUrlTree(['/login']);
};
